import { defineStore } from 'pinia'

import { i18n } from '../localization'

export type DialogType = 'Success' | 'Error' | 'Warning'

export interface DialogOptions {
    type?: DialogType | null
    hideCancel?: boolean
    confirmText?: string
    btnClass?: string
    cancelText?: string
    persistent?: boolean
    iframeUrl?: string
    fullscreen?: boolean
}

export interface DialogStore {
    showDialog: boolean
    resolve: unknown | null
    reject: unknown | null
    message: string
    title: string
    options: DialogOptions
}

export const useDialogState = defineStore('dialog', {
    state: (): DialogStore => {
        return {
            showDialog: false,
            resolve: null,
            reject: null,
            message: null,
            title: null,
            options: {
                type: null,
                hideCancel: false,
                iframeUrl: null,
                fullscreen: false,
                confirmText: i18n.global.t('dialog.confirm'),
                cancelText: i18n.global.t('dialog.cancel'),
            },
        }
    },
    actions: {
        setDefaultOptions() {
            this.options = {
                // type: 'Warning',
                hideCancel: false,
                confirmText: i18n.global.t('dialog.button.confirm'),
                cancelText: i18n.global.t('dialog.button.cancel'),
            }
        },
        confirm(): void {
            this.resolve(true)
            this.showDialog = false
        },
        cancel(): void {
            this.resolve(false)
            this.showDialog = false
        },
    },
})

export function openConfirmDialog(title: string, message: string, options: DialogOptions = {}): Promise<boolean> {
    const store = useDialogState()
    store.showDialog = true
    store.title = title
    store.message = message
    store.setDefaultOptions()
    store.options = Object.assign(store.options, options)
    return new Promise((resolve, reject) => {
        store.resolve = resolve
        store.reject = reject
    })
}

export function openIframeDialog(url: string): Promise<boolean> {
    const store = useDialogState()
    store.showDialog = true
    store.title = ''
    store.message = ''
    store.setDefaultOptions()
    store.options.hideCancel = true
    store.options.iframeUrl = url
    store.options.fullscreen = true
    store.options.confirmText = i18n.global.t('signatures.dialog_close_button')

    return new Promise((resolve, reject) => {
        store.resolve = resolve
        store.reject = reject
    })
}

export function openAlertDialog(title: string, message = '', options: DialogOptions = {}): Promise<boolean> {
    return openConfirmDialog(title, message, {
        ...options,
        type: 'Error',
        hideCancel: true,
    })
}
